// lib/locales/index.ts
// Configuración central de internacionalización

import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  I18N_NAMESPACES,
  type SupportedLocaleCode,
  type LocaleConfig,
} from "../../convex/lib/config";

// ============================================================================
// RE-EXPORTS FROM CONFIG
// ============================================================================

export {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  I18N_NAMESPACES,
  getLocaleConfig,
  isSupportedLocale,
  getValidLocale,
  getLocaleOptions,
} from "../../convex/lib/config";

export type {
  SupportedLocaleCode,
  LocaleConfig,
  I18nNamespace,
} from "../../convex/lib/config";

// ============================================================================
// LOCALE RESOURCES
// ============================================================================

// Español
import esCommon from "./es/common.json";
import esLogistics from "./es/logistics.json";
import esClients from "./es/clients.json";
import esNavigation from "./es/navigation.json";
import esInvoices from "./es/invoices.json";
import esAi from "./es/ai.json";
import esSystem from "./es/system.json";
import esSecurity from "./es/security.json";
import esSales from "./es/sales.json";
import esCatalog from "./es/catalog.json";
import esQuotations from "./es/quotations.json";
import esStock from "./es/stock.json";
import esOperations from "./es/operations.json";
import esConfiguration from "./es/configuration.json";
import esDocuments from "./es/documents.json";
import esReports from "./es/reports.json";
import esSubscriptions from "./es/subscriptions.json";
import esNotifications from "./es/notifications.json";
import esCreditNotes from "./es/credit_notes.json";
import esDebitNotes from "./es/debit_notes.json";
import esPurchasing from "./es/purchasing.json";
import esUi from "./es/ui.json";

// English
import enCommon from "./en/common.json";
import enLogistics from "./en/logistics.json";
import enClients from "./en/clients.json";
import enNavigation from "./en/navigation.json";
import enInvoices from "./en/invoices.json";
import enAi from "./en/ai.json";
import enSystem from "./en/system.json";
import enSecurity from "./en/security.json";
import enSales from "./en/sales.json";
import enCatalog from "./en/catalog.json";
import enQuotations from "./en/quotations.json";
import enStock from "./en/stock.json";
import enOperations from "./en/operations.json";
import enConfiguration from "./en/configuration.json";
import enDocuments from "./en/documents.json";
import enReports from "./en/reports.json";
import enSubscriptions from "./en/subscriptions.json";
import enNotifications from "./en/notifications.json";
import enCreditNotes from "./en/credit_notes.json";
import enDebitNotes from "./en/debit_notes.json";
import enPurchasing from "./en/purchasing.json";
import enUi from "./en/ui.json";

/**
 * Recursos de traducción organizados por idioma y namespace
 */
export const resources = {
  es: {
    common: esCommon,
    logistics: esLogistics,
    clients: esClients,
    navigation: esNavigation,
    invoices: esInvoices,
    ai: esAi,
    system: esSystem,
    security: esSecurity,
    sales: esSales,
    catalog: esCatalog,
    quotations: esQuotations,
    stock: esStock,
    operations: esOperations,
    configuration: esConfiguration,
    documents: esDocuments,
    reports: esReports,
    subscriptions: esSubscriptions,
    notifications: esNotifications,
    credit_notes: esCreditNotes,
    debit_notes: esDebitNotes,
    purchasing: esPurchasing,
    ui: esUi,
  },
  en: {
    common: enCommon,
    logistics: enLogistics,
    clients: enClients,
    navigation: enNavigation,
    invoices: enInvoices,
    ai: enAi,
    system: enSystem,
    security: enSecurity,
    sales: enSales,
    catalog: enCatalog,
    quotations: enQuotations,
    stock: enStock,
    operations: enOperations,
    configuration: enConfiguration,
    documents: enDocuments,
    reports: enReports,
    subscriptions: enSubscriptions,
    notifications: enNotifications,
    credit_notes: enCreditNotes,
    debit_notes: enDebitNotes,
    purchasing: enPurchasing,
    ui: enUi,
  },
} as const;

/**
 * Tipo de recursos disponibles
 */
export type Resources = typeof resources;

/**
 * Namespaces disponibles (extraídos de los recursos)
 */
export type AvailableNamespace = keyof (typeof resources)["es"];

// ============================================================================
// NAMESPACE CONFIGURATION
// ============================================================================

/**
 * Lista de todos los namespaces disponibles
 */
export const availableNamespaces: AvailableNamespace[] = Object.keys(
  resources[DEFAULT_LOCALE],
) as AvailableNamespace[];

/**
 * Namespace por defecto (common)
 */
export const defaultNamespace: AvailableNamespace = "common";

// ============================================================================
// I18N CONFIGURATION FOR LIBRARIES
// ============================================================================

/**
 * Configuración para react-i18next
 */
export const i18nextConfig = {
  resources,
  lng: DEFAULT_LOCALE,
  fallbackLng: FALLBACK_LOCALE,
  defaultNS: defaultNamespace,
  ns: availableNamespaces,

  interpolation: {
    escapeValue: false, // React already escapes
  },

  // Opciones de detección de idioma
  detection: {
    order: ["localStorage", "navigator"],
    lookupLocalStorage: "i18nextLng",
    caches: ["localStorage"],
  },

  // Opciones de react
  react: {
    useSuspense: true,
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Obtiene el idioma del navegador si está soportado
 */
export function getBrowserLocale(): SupportedLocaleCode {
  if (typeof navigator === "undefined") {
    return DEFAULT_LOCALE;
  }

  const browserLang = navigator.language.split("-")[0];
  const supportedCodes = SUPPORTED_LOCALES.map((l) => l.code);

  if (supportedCodes.includes(browserLang as SupportedLocaleCode)) {
    return browserLang as SupportedLocaleCode;
  }

  return DEFAULT_LOCALE;
}

/**
 * Obtiene el idioma guardado en localStorage
 */
export function getStoredLocale(): SupportedLocaleCode | null {
  if (typeof localStorage === "undefined") {
    return null;
  }

  const stored = localStorage.getItem("i18nextLng");
  if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) {
    return stored as SupportedLocaleCode;
  }

  return null;
}

/**
 * Guarda el idioma preferido en localStorage
 */
export function setStoredLocale(locale: SupportedLocaleCode): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("i18nextLng", locale);
  }
}

/**
 * Obtiene el mejor idioma disponible (guardado > navegador > default)
 */
export function getPreferredLocale(): SupportedLocaleCode {
  return getStoredLocale() ?? getBrowserLocale();
}

// ============================================================================
// TYPE DECLARATIONS FOR TYPE-SAFE TRANSLATIONS
// ============================================================================

/**
 * Declaración para mejorar el tipado de useTranslation
 * Requiere configuración adicional en el proyecto
 */
declare module "react-i18next" {
  interface CustomTypeOptions {
    defaultNS: typeof defaultNamespace;
    resources: Resources[typeof DEFAULT_LOCALE];
  }
}
